import { useState, useEffect, useRef, useCallback } from 'react';
import { normalizeVideoImagePath } from '../../utils/videoImagePath';
import { ImageData, UseVideoImageCarouselReturn } from './types';

/**
 * ビデオイメージカルーセル用のカスタムフック
 */
export const useVideoImageCarousel = (images: ImageData[]): UseVideoImageCarouselReturn => {
  // 現在のカルーセル位置の状態
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  // モーダル関連の状態
  const [modalImageIndex, setModalImageIndex] = useState<number>(0);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  // ドラッグ関連の状態
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [wasDragged, setWasDragged] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // 画像配列
  const [sortedImages, setSortedImages] = useState<ImageData[]>([]);
  const [isModalTransitioning, setIsModalTransitioning] = useState<boolean>(false);
  // スライドショーの表示方向
  const [slideDirection, setSlideDirection] = useState<string>('');
  
  // Refs
  const modalRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  
  // 追跡用の変数
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [doubleTapTimer, setDoubleTapTimer] = useState<NodeJS.Timeout | null>(null);
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialZoom, setInitialZoom] = useState<number>(1);
  
  // ドラッグ検出のしきい値（ピクセル単位）
  const dragThreshold = 5;

  // スワイプ検出のための変数
  const [swipeStart, setSwipeStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [swipeDistance, setSwipeDistance] = useState<number>(0);
  const swipeThreshold = 50; // スワイプとして検知する最小距離

  // ResizeObserver関連のエラーを抑制するための対策
  useEffect(() => {
    // ResizeObserverループエラーを抑制するための処理
    const resizeObserverError = (error: ErrorEvent): void => {
      if (error && error.message && (
        error.message.includes('ResizeObserver') || 
        error.message.includes('ResizeObserver loop completed with undelivered notifications')
      )) {
        // エラーをキャンセルして、コンソールにはログだけ残す
        error.stopImmediatePropagation();
        console.log('ResizeObserver error suppressed:', error.message);
      }
    };
    
    // イベントリスナーを追加
    window.addEventListener('error', resizeObserverError, true);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('error', resizeObserverError, true);
    };
  }, []);

  // 画像ソート
  useEffect(() => {
    if (images && images.length > 0) {
      const sorted = [...images].sort((a, b) => 
        parseInt(String(a.order_num)) - parseInt(String(b.order_num))
      );
      setSortedImages(sorted);
    }
  }, [images]);
  
  // 画像のズームと位置をリセットする関数
  const resetZoom = useCallback((): void => {
    setIsZoomed(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, []);
  
  // 位置リセット関数
  const resetPosition = useCallback((): void => {
    setPosition({ x: 0, y: 0 });
  }, []);
  
  // ズームレベルを調整する関数
  const adjustZoom = useCallback((delta: number): void => {
    setZoomLevel(prevZoom => {
      const newZoom = Math.max(1, Math.min(4, prevZoom + delta));
      if (newZoom > 1) {
        setIsZoomed(true);
      } else {
        setIsZoomed(false);
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);
  
  // モーダルを閉じる関数
  const closeModal = useCallback((): void => {
    setModalImage(null);
    resetZoom();
    document.body.style.overflow = ''; // スクロールを再有効化
  }, [resetZoom]);
  
  // 次の画像に進む関数
  const nextModalImage = useCallback((): void => {
    if (!modalImage || modalImageIndex === null || !sortedImages.length) return;
    
    const newIndex = modalImageIndex === sortedImages.length - 1 ? 0 : modalImageIndex + 1;
    setModalImageIndex(newIndex);
    setModalImage(normalizeVideoImagePath(sortedImages[newIndex].image_path));
    resetZoom();
  }, [modalImage, modalImageIndex, sortedImages, resetZoom]);
  
  // 前の画像に戻る関数
  const prevModalImage = useCallback((): void => {
    if (!modalImage || modalImageIndex === null || !sortedImages.length) return;
    
    const newIndex = modalImageIndex === 0 ? sortedImages.length - 1 : modalImageIndex - 1;
    setModalImageIndex(newIndex);
    setModalImage(normalizeVideoImagePath(sortedImages[newIndex].image_path));
    resetZoom();
  }, [modalImage, modalImageIndex, sortedImages, resetZoom]);
  
  // モーダルを開く関数
  const openModal = useCallback((imageUrl: string, index: number): void => {
    setModalImage(imageUrl);
    setModalImageIndex(index);
    resetZoom();
    document.body.style.overflow = 'hidden'; // スクロールを防止
  }, [resetZoom]);
  
  // ホイールイベント処理
  const handleWheel = useCallback((e: WheelEvent): void => {
    e.preventDefault();
    if (!modalImage) return;

    if (e.ctrlKey) {
      // Ctrlキー押下時はズーム
      const delta = -e.deltaY * 0.01;
      adjustZoom(delta);
    } else if (zoomLevel > 1) {
      // ズーム時はドラッグ移動
      const { x, y } = position;
      const newX = x - e.deltaX;
      const newY = y - e.deltaY;
      
      // 移動範囲の制限（計算省略）
      setPosition({ x: newX, y: newY });
    } else {
      // 通常時は次/前の画像
      if (e.deltaX > 50) {
        nextModalImage();
      } else if (e.deltaX < -50) {
        prevModalImage();
      }
    }
  }, [modalImage, position, zoomLevel, adjustZoom, nextModalImage, prevModalImage]);
  
  // マウスドラッグ開始
  const handleMouseDown = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    if (!modalImage || zoomLevel <= 1) return;
    
    setIsDragging(true);
    setWasDragged(false);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [modalImage, position, zoomLevel]);
  
  // マウスドラッグ中
  const handleMouseMove = useCallback((e: React.MouseEvent): void => {
    if (!isDragging) return;
    
    setWasDragged(true);
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setPosition({
      x: newX,
      y: newY
    });
  }, [isDragging, dragStart]);
  
  // マウスドラッグ終了
  const handleMouseUp = useCallback((): void => {
    setIsDragging(false);
  }, []);
  
  // マウスがモーダル外に出た時
  const handleMouseLeave = useCallback((): void => {
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);
  
  // タッチイベント用の関数
  // タッチ開始
  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    // ターゲット要素がボタンの場合は通常の処理を許可
    if ((e.target as HTMLElement).closest('.modal-close-button') || 
        (e.target as HTMLElement).closest('.modal-control-button') || 
        (e.target as HTMLElement).closest('.modal-nav-button')) {
      return;
    }
    
    // スワイプ開始位置を記録
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setSwipeStart({ x: touch.clientX, y: touch.clientY });
      setSwipeDistance(0);
    }
    
    // ズームしていない場合はデフォルトの動作を許可（スクロール可能にする）
    if (!isZoomed) {
      return;
    }
    
    e.preventDefault();
    
    if (e.touches.length === 1) {
      // シングルタッチ（ドラッグ/タップ）
      setWasDragged(false);
      
      const touch = e.touches[0];
      
      // ダブルタップ検出
      const now = Date.now();
      const timeSinceLastTap = now - lastTapTime;
      
      if (timeSinceLastTap < 300) {
        // ダブルタップ検出
        if (zoomLevel === 1) {
          adjustZoom(1); // ズームイン
        } else {
          resetZoom(); // リセット
        }
      }
      
      setLastTapTime(now);
      
      // ドラッグ開始
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    } else if (e.touches.length === 2) {
      // ピンチズーム準備
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      setInitialPinchDistance(distance);
      setInitialZoom(zoomLevel);
    }
  }, [adjustZoom, lastTapTime, position, resetZoom, zoomLevel, isZoomed]);
  
  // タッチ移動
  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    // スワイプ距離を計算
    if (e.touches.length === 1 && !isZoomed) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - swipeStart.x;
      setSwipeDistance(deltaX);
    }
    
    // ズームしていない状態ではスクロールを妨げない
    if (!isZoomed && e.touches.length === 1) {
      return;
    }
    
    e.preventDefault();
    
    if (e.touches.length === 1 && isDragging) {
      // ドラッグ移動
      setWasDragged(true);
      
      const touch = e.touches[0];
      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;
      
      setPosition({ x: newX, y: newY });
    } else if (e.touches.length === 2 && initialPinchDistance) {
      // ピンチズーム
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      const scaleFactor = distance / initialPinchDistance;
      const newZoom = Math.max(1, Math.min(4, initialZoom * scaleFactor));
      
      setZoomLevel(newZoom);
      if (newZoom > 1) {
        setIsZoomed(true);
      }
    }
  }, [dragStart, initialPinchDistance, initialZoom, isDragging, isZoomed, swipeStart]);
  
  // タッチ終了
  const handleTouchEnd = useCallback((e: React.TouchEvent): void => {
    // スワイプナビゲーション処理
    if (!isZoomed && Math.abs(swipeDistance) > swipeThreshold) {
      // 水平方向のスワイプを検出
      if (swipeDistance > 0) {
        // 右へのスワイプ -> 前の画像
        prevModalImage();
      } else {
        // 左へのスワイプ -> 次の画像
        nextModalImage();
      }
    }
    
    setIsDragging(false);
    setInitialPinchDistance(null);
    setSwipeDistance(0);
  }, [isZoomed, prevModalImage, nextModalImage, swipeDistance, swipeThreshold]);
  
  // ズームトグル
  const toggleZoom = useCallback((e: React.MouseEvent): void => {
    if (isZoomed) {
      resetZoom();
    } else {
      setIsZoomed(true);
      setZoomLevel(2);
    }
  }, [isZoomed, resetZoom]);

  // ズームレベルが変更されたときにドラッグ位置をリセット
  useEffect(() => {
    if (zoomLevel === 1) {
      resetPosition();
    }
  }, [zoomLevel, resetPosition]);

  // タッチイベントリスナー
  useEffect(() => {
    if (modalImage && modalRef.current) {
      // モーダル画像へのタッチイベント設定
      const modalContainer = modalRef.current;
      const imageElement = imageRef.current;
      
      // イベントハンドラを関数として定義
      const touchStartHandler = (e: TouchEvent): void => {
        const reactTouchEvent = e as unknown as React.TouchEvent;
        handleTouchStart(reactTouchEvent);
      };
      const touchMoveHandler = (e: TouchEvent): void => {
        const reactTouchEvent = e as unknown as React.TouchEvent;
        handleTouchMove(reactTouchEvent);
      };
      const touchEndHandler = (e: TouchEvent): void => {
        const reactTouchEvent = e as unknown as React.TouchEvent;
        handleTouchEnd(reactTouchEvent);
      };
      
      // 画像要素にイベントリスナーを追加
      if (imageElement) {
        imageElement.addEventListener('touchstart', touchStartHandler, { passive: false });
        imageElement.addEventListener('touchmove', touchMoveHandler, { passive: false });
        imageElement.addEventListener('touchend', touchEndHandler);
      }
      
      // モーダルコンテナにもイベントリスナーを追加（画像の外側でも処理できるように）
      modalContainer.addEventListener('touchstart', touchStartHandler, { passive: false });
      modalContainer.addEventListener('touchmove', touchMoveHandler, { passive: false });
      modalContainer.addEventListener('touchend', touchEndHandler);

      return () => {
        // クリーンアップ
        if (imageElement) {
          imageElement.removeEventListener('touchstart', touchStartHandler);
          imageElement.removeEventListener('touchmove', touchMoveHandler);
          imageElement.removeEventListener('touchend', touchEndHandler);
        }
        modalContainer.removeEventListener('touchstart', touchStartHandler);
        modalContainer.removeEventListener('touchmove', touchMoveHandler);
        modalContainer.removeEventListener('touchend', touchEndHandler);
      };
    }
  }, [modalImage, handleTouchStart, handleTouchMove, handleTouchEnd, imageRef, modalRef]);

  // キーボードナビゲーション
  useEffect(() => {
    if (!modalImage) return;
    
    const keydownHandler = (e: KeyboardEvent): void => {
      switch(e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          prevModalImage();
          break;
        case 'ArrowRight':
          nextModalImage();
          break;
        case '+':
        case '=':
          adjustZoom(0.1);
          break;
        case '-':
          adjustZoom(-0.1);
          break;
        case '0':
          resetZoom();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', keydownHandler);
    return () => window.removeEventListener('keydown', keydownHandler);
  }, [modalImage, closeModal, prevModalImage, nextModalImage, adjustZoom, resetZoom]);

  // ホイールイベントリスナー
  useEffect(() => {
    if (modalImage && modalRef.current) {
      const currentModalRef = modalRef.current;
      currentModalRef.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        currentModalRef.removeEventListener('wheel', handleWheel);
      };
    }
  }, [modalImage, handleWheel]);

  // カルーセルの前後ナビゲーション
  const prevImage = useCallback((): void => {
    const newIndex = currentImageIndex === 0 ? sortedImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
  }, [currentImageIndex, sortedImages.length]);

  const nextImage = useCallback((): void => {
    const newIndex = currentImageIndex === sortedImages.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
  }, [currentImageIndex, sortedImages.length]);

  // モーダル内のコンテンツがクリックされたときの処理
  const handleModalContentClick = (e: React.MouseEvent): void => {
    // オーバーレイへのクリック伝播を常に止める
    e.stopPropagation();
  };

  return {
    // 状態変数
    currentImageIndex,
    modalImage,
    modalImageIndex,
    isZoomed,
    zoomLevel,
    isDragging,
    position,
    sortedImages,
    slideDirection,
    isModalTransitioning,
    swipeDistance,
    
    // Refs
    modalRef,
    imageRef,
    carouselRef,
    
    // 関数
    prevImage,
    nextImage,
    openModal,
    closeModal,
    prevModalImage,
    nextModalImage,
    toggleZoom,
    adjustZoom,
    resetZoom,
    
    // ハンドラー
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleModalContentClick,
    
    // 定数
    swipeThreshold
  };
}; 
